import { Box, CircularProgress, Grid2 as Grid, Skeleton, Typography } from "@mui/material"
import { formatPrice } from "../../../../../utils/helper"

/**
 * WalletSummaryCards
 *
 * 3 (or 4) stat cards: Pending, Available, Total Earned, Commission Held
 * Shows a disabled overlay when onboarding isn't complete.
 */
function WalletSummaryCards({ wallet, loading, error, onboardingComplete }) {
  if (loading && !wallet) {
    return (
      <Grid container spacing={2} mb={2}>
        {Array.from({ length: 3 }, (_, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: "14px" }} />
          </Grid>
        ))}
      </Grid>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: "12px",
          bgcolor: "#fff5f5",
          border: "1px solid #fecaca",
          color: "#b91c1c",
          fontSize: "13px",
        }}
      >
        Failed to load wallet data: {error}
      </Box>
    )
  }

  const cards = [
    {
      label: "Total Earned",
      value: wallet?.totalEarned ?? 0,
      icon: "📈",
      gradient: "linear-gradient(135deg,#ede9fe,#ddd6fe)",
      textColor: "#4c1d95",
      iconBg: "#8b5cf6",
      tip: "Lifetime earnings (after commission)",
    },
    // {
    //   label: "Available Balance",
    //   value: wallet?.availableBalance ?? 0,
    //   icon: "💰",
    //   gradient: "linear-gradient(135deg,#d1fae5,#a7f3d0)",
    //   textColor: "#065f46",
    //   iconBg: "#34d399",
    //   tip: "Ready for payout",
    // },
    {
      label: "Pending Balance",
      value: wallet?.pendingBalance ?? 0,
      icon: "⏳",
      gradient: "linear-gradient(135deg,#fef3c7,#fde68a)",
      textColor: "#92400e",
      iconBg: "#fbbf24",
      tip: "Earnings held until service completion",
    },
    {
      label: "Platform Fee",
      value: wallet?.commissionPercent ?? 0,
      icon: "⚖️",
      gradient: "linear-gradient(135deg,#fecaca,#fca5a5)",
      textColor: "#b91c1c",
      iconBg: "#f87171",
      tip: "Platform fee withheld from earnings",
      isPercent: true,
    },

  ]

  return (
    <Grid container spacing={2} mb={1} sx={{ position: "relative" }}>
      {cards.map((card) => (
        <Grid key={card.label} size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            {...card}
            disabled={!onboardingComplete}
            currency={wallet?.currency || "inr"}
          />
        </Grid>
      ))}

      {/* Payouts disabled overlay */}
      {!onboardingComplete && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "14px",
            backdropFilter: "blur(2px)",
            bgcolor: "rgba(255,255,255,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              py: 1,
              px: 2.5,
              bgcolor: "white",
              borderRadius: "10px",
              border: "1.5px dashed #d1d5db",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography fontSize="13px" color="text.secondary" fontWeight={600}>
              🔒 Complete onboarding to unlock payouts
            </Typography>
          </Box>
        </Box>
      )}
    </Grid>
  )
}

function StatCard({ label, value, icon, gradient, textColor, iconBg, tip, disabled, currency, isPercent }) {
  const formatted = isPercent
    ? `${value}%`
    : formatPrice((value || 0) / 100, currency)

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "14px",
        background: disabled ? "#f5f5f5" : gradient,
        border: "1px solid",
        borderColor: disabled ? "#e5e7eb" : "transparent",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": disabled
          ? {}
          : { transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" },
        filter: disabled ? "grayscale(0.4)" : "none",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography
            fontSize="12px"
            fontWeight={600}
            color={disabled ? "text.secondary" : textColor}
            sx={{ opacity: 0.75, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}
          >
            {label}
          </Typography>
          <Typography
            fontSize="26px"
            fontWeight={800}
            color={disabled ? "text.secondary" : textColor}
            lineHeight={1.1}
          >
            {formatted}
          </Typography>
          {tip && (
            <Typography fontSize="11px" color={disabled ? "text.disabled" : textColor} sx={{ opacity: 0.65, mt: 0.5 }}>
              {tip}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            bgcolor: disabled ? "#d1d5db" : iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </Box>
  )
}

export default WalletSummaryCards
